// src/services/teamService.ts
//
// All Supabase access for the Settings → Team screen (Story 1.4). Two kinds
// of operations live here:
//   - List / position / attribute writes are admin config: direct table
//     access under RLS (spine AD-3). The SELECT policies cover all org members;
//     the write policies require an admin role.
//   - Create / deactivate / reactivate are Supabase Auth admin operations
//     (invite, ban, unban) — impossible direct-under-RLS with the anon key —
//     so they go through the admin-users edge function via functions.invoke.
//
// Components/hooks consume the clean src/types/yak.ts types only; results are
// cast once per method. DB error codes and the edge function's enumerated
// codes are mapped to stable human copy here — raw Postgres/function text
// never reaches the UI (the 1.1 throwMapped contract).
//
// Contract note for Story 1.5: listTeam() is the users/positions read 1.5
// should reuse for its position <datalist> and no-match warning — active =
// `status === 'active'`, position compared trimmed exact-match.

import { supabase } from '../lib/supabase'
import type {
  YakTeamMember,
  YakUserAttribute,
  YakUserAttributeType,
} from '../types/yak'

const DUPLICATE_ATTRIBUTE_MESSAGE =
  'That value is already assigned to this user'
const DUPLICATE_EMAIL_MESSAGE = 'A user with this email already exists'
const REGION_MISSING_MESSAGE =
  'That region no longer exists — refresh and try again'
const INVALID_VALUE_MESSAGE = "That value isn't valid — check it and try again"
const STALE_ROW_MESSAGE =
  'That user no longer exists — it may have been changed by someone else. Refresh and try again'
const EMPTY_VALUE_MESSAGE = 'Value cannot be empty'
const NO_ORG_MESSAGE = 'Your account is not linked to an organization'

// Copy for the admin-users edge function's enumerated failure codes.
const FUNCTION_MESSAGES: Record<string, string> = {
  email_exists: DUPLICATE_EMAIL_MESSAGE,
  self_deactivate: "You can't deactivate your own account",
  not_authorized: "You don't have permission to do that",
  invalid_email: 'Enter a valid email address',
  invalid_name: 'First and last name are required',
  not_found: STALE_ROW_MESSAGE,
  // Story 4.4: the user was deactivated but the open-ticket reassignment cascade
  // failed. Retrying deactivate finishes it (the DB cascade is idempotent).
  reassign_failed:
    "Deactivated, but their open tickets couldn't be reassigned — retry deactivation to finish",
}
const FUNCTION_FALLBACK_MESSAGE = 'Something went wrong — please try again'

interface PostgrestErrorLike {
  message: string
  code?: string
}

type WriteSurface = 'attribute' | 'position'

// Exported for unit testing — the mapping is the concurrency-safety contract
// for direct-under-RLS writes (another admin changed the row between load and
// save; a duplicate slipped past the client check).
export function throwMapped(
  error: PostgrestErrorLike,
  surface: WriteSurface
): never {
  if (error.code === '23505') throw new Error(DUPLICATE_ATTRIBUTE_MESSAGE)
  if (error.code === '23503') throw new Error(REGION_MISSING_MESSAGE)
  if (error.code === '23514') {
    throw new Error(
      surface === 'position' ? EMPTY_VALUE_MESSAGE : INVALID_VALUE_MESSAGE
    )
  }
  if (error.code === 'PGRST116') throw new Error(STALE_ROW_MESSAGE)
  throw new Error(error.message)
}

// Exported for unit testing — the admin-users function returns { code } on a
// non-2xx response; this renders each enumerated code as stable copy.
export function mapFunctionCode(code: string | null | undefined): string {
  if (code && FUNCTION_MESSAGES[code]) return FUNCTION_MESSAGES[code]
  return FUNCTION_FALLBACK_MESSAGE
}

async function currentOrgId(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const organizationId = session?.user?.app_metadata?.organization_id ?? null
  if (!organizationId) throw new Error(NO_ORG_MESSAGE)
  return organizationId
}

// functions.invoke reports a non-2xx response as a FunctionsHttpError whose
// `context` is the raw Response — read our enumerated { code } out of it and
// map to human copy. Falls back gracefully if the body isn't our shape.
async function throwFunctionError(error: unknown): Promise<never> {
  const context = (error as { context?: unknown })?.context
  if (context instanceof Response) {
    try {
      const body = await context.json()
      throw new Error(mapFunctionCode(body?.code))
    } catch (parseErr) {
      if (
        parseErr instanceof Error &&
        parseErr.message !== FUNCTION_FALLBACK_MESSAGE
      ) {
        // A thrown mapped message — re-surface it.
        if (Object.values(FUNCTION_MESSAGES).includes(parseErr.message))
          throw parseErr
      }
      throw new Error(FUNCTION_FALLBACK_MESSAGE)
    }
  }
  throw new Error(FUNCTION_FALLBACK_MESSAGE)
}

class TeamService {
  // The org's console users (market_access_user + admin) with their position.
  // Read directly under the existing user_profiles_same_org_read SELECT policy
  // (NOT the legacy get_organization_users RPC — it lacks `position`). Scoped
  // to the caller's org explicitly; RLS would refuse cross-org rows anyway.
  async listTeam(): Promise<YakTeamMember[]> {
    const organizationId = await currentOrgId()
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, first_name, last_name, role, status, position')
      .eq('organization_id', organizationId)
      .in('role', ['market_access_user', 'admin'])
      .order('first_name', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakTeamMember[] | null) || []
  }

  // All attribute-assignment rows for the org (RLS scopes to the caller's
  // org); the UI groups them by user + type.
  async listAttributes(): Promise<YakUserAttribute[]> {
    const { data, error } = await supabase
      .from('yak_user_attributes')
      .select('*')

    if (error) throw new Error(error.message)
    return (data as unknown as YakUserAttribute[] | null) || []
  }

  // Position is free text on the MA user; empty/whitespace collapses to NULL
  // (an unset position, not a blank one — the CHECK forbids blank).
  async updatePosition(userId: string, position: string | null): Promise<void> {
    const trimmed = position?.trim() || null
    const { error } = await supabase
      .from('user_profiles')
      .update({ position: trimmed })
      .eq('id', userId)

    if (error) throwMapped(error, 'position')
  }

  // Add an assignment. For region, `value` is the region node's id (FK); for
  // brand/channel/payer it's the trimmed text value. Rows are add-only — the
  // DB rejects duplicates (case-insensitive) and cross-org region refs.
  async addAttribute(
    userId: string,
    type: YakUserAttributeType,
    value: string
  ): Promise<void> {
    const organizationId = await currentOrgId()

    let row: {
      organization_id: string
      user_id: string
      attribute_type: YakUserAttributeType
      value: string | null
      region_id: string | null
    }
    if (type === 'region') {
      if (!value) throw new Error(EMPTY_VALUE_MESSAGE)
      row = {
        organization_id: organizationId,
        user_id: userId,
        attribute_type: 'region',
        region_id: value,
        value: null,
      }
    } else {
      const trimmed = value.trim()
      if (!trimmed) throw new Error(EMPTY_VALUE_MESSAGE)
      row = {
        organization_id: organizationId,
        user_id: userId,
        attribute_type: type,
        value: trimmed,
        region_id: null,
      }
    }

    const { error } = await supabase.from('yak_user_attributes').insert(row)
    if (error) throwMapped(error, 'attribute')
  }

  // Remove an assignment by row id ("editing" an assignment is remove + add —
  // the table has no UPDATE policy).
  async removeAttribute(attributeId: string): Promise<void> {
    const { error } = await supabase
      .from('yak_user_attributes')
      .delete()
      .eq('id', attributeId)

    if (error) throwMapped(error, 'attribute')
  }

  // Create a console login via the admin-users edge function (invite email).
  // Returns the created profile so the list can render it immediately.
  async createUser(input: {
    email: string
    first_name: string
    last_name: string
    position?: string | null
  }): Promise<YakTeamMember> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      body: { action: 'create', ...input },
    })
    if (error) await throwFunctionError(error)
    return (data as { data: YakTeamMember }).data
  }

  // Deactivate a console login and re-resolve their open/answered tickets by
  // rule (Story 4.4's cascade, run inside the edge function). Returns the number
  // of tickets reassigned so the confirmation can state the consequence
  // delivered (fulfilling 1.4's "their open tickets will be reassigned by rule").
  async deactivateUser(userId: string): Promise<number> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      body: { action: 'deactivate', user_id: userId },
    })
    if (error) await throwFunctionError(error)
    return (
      (data as { data?: { reassigned_count?: number } })?.data
        ?.reassigned_count ?? 0
    )
  }

  async reactivateUser(userId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('admin-users', {
      body: { action: 'reactivate', user_id: userId },
    })
    if (error) await throwFunctionError(error)
  }
}

export const teamService = new TeamService()
